import React from "react";
import { toast } from "react-toastify";

export interface DuplicateInfo {
    type: string;
    id?: number;
    name?: string;
    referenceNumber?: string;
}

const ENTITY_URL_MAP: Record<string, string> = {
    provider: "/provider/",
    client: "/client/",
    company: "/company/",
    contact: "/contact/",
    organization: "/company/",
    user: "/user/",
    mission: "/mission/",
    missionRequest: "/mission-request/",
    // No itinerary route: an itinerary lives inside its mission, so it shows as text.
    invoice: "/finance/invoice/",
    bill: "/finance/bill/",
    creditNote: "/finance/credit-note/",
    payment: "/finance/payment/",
    journalEntry: "/finance/journal-entry/",
    account: "/finance/account/",
    task: "/tasks/",
    taskTemplate: "/task-templates/",
    lead: "/lead/",
};

const ENTITY_LABEL_MAP: Record<string, string> = {
    provider: "Provider",
    client: "Client",
    company: "Company",
    contact: "Contact",
    organization: "Organization",
    user: "User",
    mission: "Mission",
    missionRequest: "Mission request",
    itinerary: "Itinerary",
    invoice: "Invoice",
    bill: "Bill",
    creditNote: "Credit note",
    payment: "Payment",
    journalEntry: "Journal entry",
    account: "Account",
    task: "Task",
    taskTemplate: "Task template",
    lead: "Lead",
};

const renderDuplicateLink = (dup: DuplicateInfo, index: number) => {
    const url = (ENTITY_URL_MAP[dup.type] ?? "") + (dup.id ?? "");
    const label = ENTITY_LABEL_MAP[dup.type] || dup.type;
    const linkText = dup.name
        ? `${dup.name}${dup.referenceNumber ? ` (${dup.referenceNumber})` : ""}`
        : label;

    if (!ENTITY_URL_MAP[dup.type] || !dup.id) {
        return (
            <div key={index} style={{ color: "var(--text-muted)", fontSize: "var(--fs-xs)" }}>
                {label}: {linkText}
            </div>
        );
    }

    return (
        <div key={index} style={{ marginTop: 2 }}>
            <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-xs)" }}>View {label}: </span>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    color: "var(--status-info-solid)",
                    textDecoration: "underline",
                    fontWeight: 600,
                    fontSize: "var(--fs-xs)",
                }}
            >
                {linkText}
            </a>
        </div>
    );
};

/**
 * Shows a toast error with a clickable link (or list of links) to the duplicate entity.
 * Falls back to a plain text toast if no duplicate info is present.
 */
export const showDuplicateError = (body: any, fallback: string): void => {
    const message = extractMessage(body, fallback);
    const rawDuplicate: DuplicateInfo | DuplicateInfo[] | undefined = body?.duplicate;
    const duplicates: DuplicateInfo[] = Array.isArray(rawDuplicate)
        ? rawDuplicate.filter(d => d && d.type)
        : rawDuplicate && rawDuplicate.type
            ? [rawDuplicate]
            : [];

    if (duplicates.length > 0) {
        toast.error(
            <div style={{ fontSize: "var(--fs-xs)" }}>
                <div style={{ color: "var(--status-danger-solid)", marginBottom: 6 }}>{message}</div>
                {duplicates.map(renderDuplicateLink)}
            </div>,
            { autoClose: false }
        );
    } else {
        toast.error(message);
    }
};

function extractMessage(body: any, fallback: string): string {
    if (!body) return fallback;
    if (typeof body === "string") return body !== "" ? body : fallback;
    if (typeof body?.message === "string" && body.message !== "") return body.message;
    return fallback;
}
